import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DocxFolderSource } from '@/analytics/enums/docx-export.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackDocxExportsFolderOpened } from '@/analytics/utils/docx-export-analytics'
import { getDocxExportsFolder } from '@/agent-v2/docx-export/fetch/get-docx-exports-folder'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { matterPaths } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'

/** Opens the matter's "Draft .docx exports" folder in Matter Files; it exists once an export is filed. */
export function useOpenDocxExportsFolder(matterId: string | undefined, conversationId?: string) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  return useCallback(async () => {
    if (!matterId) return
    try {
      const folderId = await getDocxExportsFolder(matterId)
      if (folderId) {
        navigate(matterPaths.userFiles.folder(matterId, folderId))
        trackDocxExportsFolderOpened(trackEvent, {
          docxFolderSource: DocxFolderSource.DraftMenu,
          ...(conversationId ? { conversationId } : {}),
        })
        return
      }
      dispatch(
        openGlobalToast({
          type: GlobalToastType.INFO,
          message: 'No .docx exports yet. Add a template, then use Export as .docx.',
          durationMs: kToastDurationMediumMs,
        })
      )
    } catch {
      dispatch(
        openGlobalToast({ type: GlobalToastType.ERROR, message: 'Could not open your .docx exports. Please try again.', durationMs: kToastDurationShortMs })
      )
    }
  }, [conversationId, dispatch, matterId, navigate, trackEvent])
}
